/**
 * Assessment Service
 */

import { httpClient } from '../http-client';
import type {
  AssessmentType,
  AssessmentScale,
  QuizCategory,
  QuizFormat,
  DifficultyLevel,
  QuizResponseInput,
} from '@careertree/shared';

const API_BASE_URL = '/api/v1/assessments';

// ===== Types =====

export interface CreateAssessmentInput {
  parentNodeId: string;
  assessmentType: AssessmentType;
  assessmentScale: AssessmentScale;
  title?: string;
}

export interface CreateAssessmentResult {
  assessmentNodeId: string;
  workflowId: string;
}

export interface AssessmentNodeInfo {
  id: string;
  title: string;
  description: string;
  assessmentType: AssessmentType;
  assessmentScale: AssessmentScale;
  contentStatus: 'pending' | 'queued' | 'generating' | 'completed' | 'failed';
  parentId: string;
  parentTitle: string;
  treeTitle: string;
  hasContent: boolean;
  contentId?: string;
  totalItems: number;
  attemptCount: number;
  lastAttempt?: {
    score: number;
    totalScore: number;
    percentage: number;
    completedAt: string;
  };
  bestScore?: number;
}

export interface QuizItemForClient {
  id: string;
  order: number;
  category: QuizCategory;
  format: QuizFormat;
  difficulty: DifficultyLevel;
  questionText: string;
  options: Array<{ key: string; text: string }> | null;
  hints: string[];
}

export interface QuizItemsResponse {
  contentId: string;
  totalItems: number;
  items: QuizItemForClient[];
}

export interface SubmitQuizInput {
  contentId: string;
  responses: QuizResponseInput[];
  totalTimeSeconds: number;
}

export interface SubmitQuizResult {
  attemptId: string;
  score: number;
  totalScore: number;
  percentage: number;
  categoryScores: Record<string, { correct: number; total: number }>;
  difficultyScores: Record<string, { correct: number; total: number }>;
  results: Array<{
    quizItemId: string;
    isCorrect: boolean;
    correctAnswer: unknown;
    explanation: string;
  }>;
}

export interface AttemptHistoryItem {
  id: string;
  score: number;
  totalScore: number;
  percentage: number;
  categoryScores: Record<string, { correct: number; total: number }>;
  difficultyScores: Record<string, { correct: number; total: number }>;
  totalTimeSeconds: number;
  completedAt: string;
  responses: Array<{
    quizItemId: string;
    questionText: string;
    category: QuizCategory;
    difficulty: DifficultyLevel;
    userAnswer: unknown;
    isCorrect: boolean;
    timeSpentSeconds: number;
    hintsUsed: number;
  }>;
}

export interface AttemptsResponse {
  nodeId: string;
  totalAttempts: number;
  attempts: AttemptHistoryItem[];
}

export interface RegenerateResult {
  workflowId: string;
}

// ===== Service =====

export const AssessmentService = {
  /**
   */
  async createAssessment(input: CreateAssessmentInput): Promise<CreateAssessmentResult> {
    try {
      const response = await httpClient.post<CreateAssessmentResult>(API_BASE_URL, input);
      return response.data;
    } catch (error) {
      console.error('[AssessmentService] Create assessment error:', error);
      throw error;
    }
  },

  /**
   */
  async getAssessment(nodeId: string): Promise<AssessmentNodeInfo> {
    try {
      const response = await httpClient.get<AssessmentNodeInfo>(`${API_BASE_URL}/${nodeId}`);
      return response.data;
    } catch (error) {
      console.error('[AssessmentService] Get assessment error:', error);
      throw error;
    }
  },

  /**
   */
  async getQuizItems(nodeId: string): Promise<QuizItemsResponse> {
    try {
      const response = await httpClient.get<QuizItemsResponse>(`${API_BASE_URL}/${nodeId}/quiz-items`);
      return response.data;
    } catch (error) {
      console.error('[AssessmentService] Get quiz items error:', error);
      throw error;
    }
  },

  /**
   */
  async submitQuiz(nodeId: string, input: SubmitQuizInput): Promise<SubmitQuizResult> {
    try {
      const response = await httpClient.post<SubmitQuizResult>(`${API_BASE_URL}/${nodeId}/submit`, input);
      return response.data;
    } catch (error) {
      console.error('[AssessmentService] Submit quiz error:', error);
      throw error;
    }
  },

  /**
   */
  async getAttempts(nodeId: string): Promise<AttemptsResponse> {
    try {
      const response = await httpClient.get<AttemptsResponse>(`${API_BASE_URL}/${nodeId}/attempts`);
      return response.data;
    } catch (error) {
      console.error('[AssessmentService] Get attempts error:', error);
      throw error;
    }
  },

  /**
   */
  async regenerateAssessment(nodeId: string): Promise<RegenerateResult> {
    try {
      const response = await httpClient.post<RegenerateResult>(`${API_BASE_URL}/${nodeId}/regenerate`);
      return response.data;
    } catch (error) {
      console.error('[AssessmentService] Regenerate assessment error:', error);
      throw error;
    }
  },
};
